import React, { useState, useEffect, useRef } from 'react';
import InviteModal from './components/InviteModal';

import { db } from './firebase';
import { collection, query, getDocs, orderBy } from './firestore-compat';
import { timeToMinutes, minutesToTime } from './utils/conflictUtils';
import AvailabilityPanel from './components/AvailabilityPanel';

function AddAuditoriaModal({
  isOpen,
  onClose,
  onAddAuditoria,
  onUpdateAuditoria,
  onDeleteAuditoria,
  onStatusChange,
  selectedDate,
  editingAuditoria,
  unidadesConfig = [], // Recebe lista de objetos {id, name, color}
  auditoresConfig = [], // Recebe lista de objetos {id, name, color}
  type = 'qualidade', // 'qualidade', 'homecare', 'pessoal'
  disableSystemInvite = false, // Se true, não marca como convite de sistema mesmo tendo participantes
  initialValues = null, // Dados para pré-preencher em modo criação (ex: vindo de um convite)
  submitLabel = null, // Texto personalizado para o botão de salvar
  userName = '', // Nome do usuário atual para auto-seleção
  canPublishQualidade = false, // Se pode publicar na Agenda da Qualidade
  canPublishHomecare = false,  // Se pode publicar na Agenda Homecare pública
}) {
  const CATEGORIAS = React.useMemo(() => type === 'qualidade'
    ? ['Auditoria', 'Treinamento', 'Tarefa Interna']
    : ['Agendamento', 'Treinamento', 'Tarefa Interna'], [type]);

  // Evita reinicializar o formulário quando auditoresConfig carrega DEPOIS que o modal já abriu
  const initKeyRef = useRef(null);

  // Estado para multiselect
  const [selectedAuditores, setSelectedAuditores] = useState([]);
  const [systemUsers, setSystemUsers] = useState([]);
  const [selectedInternalParticipants, setSelectedInternalParticipants] = useState([]);

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [generatedInviteText, setGeneratedInviteText] = useState('');
  const [showParticipantPicker, setShowParticipantPicker] = useState(false);
  const [participantSearch, setParticipantSearch] = useState('');

  // Estados para status (Realizado/Não Realizado)
  const [showJustificativa, setShowJustificativa] = useState(false);
  const [justificativaText, setJustificativaText] = useState('');
  
  const [confirmDelete, setConfirmDelete] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [descricao, setDescricao] = useState('');
  const [categoria, setCategoria] = useState(CATEGORIAS[0]);
  const [setor, setSetor] = useState('');
  const [unidade, setUnidade] = useState(''); // Armazena Nome da Unidade
  const [horario, setHorario] = useState('09:00');
  const [horarioFim, setHorarioFim] = useState('10:00');
  const [showAvailabilityPanel, setShowAvailabilityPanel] = useState(false);
  const [participantes, setParticipantes] = useState('');
  const [data, setData] = useState(new Date());

  const [isRecorrente, setIsRecorrente] = useState(false);
  const [frequencia, setFrequencia] = useState('semanal');
  const [terminaEm, setTerminaEm] = useState('');
  const [publicarQualidade, setPublicarQualidade] = useState(false);
  const [publicarHomecare, setPublicarHomecare] = useState(false);

  useEffect(() => {
    const fetchUsers = async () => {
      const q = query(collection(db, 'users'), orderBy('name'));
      const snap = await getDocs(q);
      setSystemUsers(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    };
    fetchUsers();
  }, []);

  const equipesDisponiveis = React.useMemo(() => {
    const equipes = systemUsers.map(u => u.equipe).filter(Boolean);
    return [...new Set(equipes)].sort();
  }, [systemUsers]);

  useEffect(() => {
    if (!isOpen) {
      initKeyRef.current = null; // reseta ao fechar
      return;
    }
    // Chave única para a sessão atual: ID do evento sendo editado ou 'new'
    const sessionKey = editingAuditoria?.id || (selectedDate ? selectedDate.toISOString() : 'new');
    if (initKeyRef.current === sessionKey) return; // já inicializado — não apaga o que o usuário digitou
    initKeyRef.current = sessionKey;

    if (isOpen) {
      if (editingAuditoria) {
        // ... (lógica existente de edição) ...
        setData(new Date(editingAuditoria.data + 'T00:00:00'));
        if (editingAuditoria.auditores && Array.isArray(editingAuditoria.auditores)) {
          setSelectedAuditores(editingAuditoria.auditores);
        } else {
          setSelectedAuditores(editingAuditoria.auditor ? [editingAuditoria.auditor] : []);
        }
        setDescricao(editingAuditoria.descricao || editingAuditoria.auditoria || '');
        setCategoria(editingAuditoria.categoria || CATEGORIAS[0]);
        setSetor(editingAuditoria.setor || '');
        setUnidade(editingAuditoria.unidade || (unidadesConfig.length > 0 ? unidadesConfig[0].name : ''));
        setHorario(editingAuditoria.horario || '09:00');
        setHorarioFim(editingAuditoria.horarioFim || (editingAuditoria.horario ? minutesToTime(timeToMinutes(editingAuditoria.horario) + 60) : '10:00'));
        setParticipantes(editingAuditoria.participantes || '');
        setSelectedInternalParticipants(editingAuditoria.participantesUids || []);
        setIsRecorrente(false);
      } else if (initialValues) {
        // MODO CRIAÇÃO COM DADOS INICIAIS (Convite -> Evento Pessoal)
        setData(initialValues.data ? new Date(initialValues.data + 'T00:00:00') : (selectedDate || new Date()));

        // Preservar auditores/responsáveis do convite
        if (initialValues.auditores && initialValues.auditores.length > 0) {
          setSelectedAuditores(initialValues.auditores);
        } else {
          // Se não houver auditores no convite, deixa vazio para o usuário escolher
          setSelectedAuditores([]);
        }

        setDescricao(initialValues.descricao || initialValues.auditoria || '');
        setCategoria(initialValues.categoria || CATEGORIAS[0]);
        setSetor(initialValues.setor || '');
        setUnidade(initialValues.unidade || (unidadesConfig.length > 0 ? unidadesConfig[0].name : ''));
        setHorario(initialValues.horario || '09:00');
        setHorarioFim(initialValues.horarioFim || (initialValues.horario ? minutesToTime(timeToMinutes(initialValues.horario) + 60) : '10:00'));

        // Participantes: Se vier do convite, pode colocar no campo de texto para referência
        setParticipantes(initialValues.participantes || '');
        setSelectedInternalParticipants(initialValues.participantesUids || []);

        setIsRecorrente(false);
        setFrequencia('semanal');
        const dataFinalPadrao = new Date();
        dataFinalPadrao.setMonth(dataFinalPadrao.getMonth() + 1);
        setTerminaEm(dataFinalPadrao.toISOString().split('T')[0]);
      } else {
        // MODO CRIAÇÃO PADRÃO
        setData(selectedDate || new Date());

        // AUTO-SELEÇÃO DO AUDITOR LOGADO
        // Se o usuário logado estiver na lista de responsáveis, seleciona ele automaticamente.
        const myAuditorProfile = auditoresConfig.find(a => a.name === userName);
        if (myAuditorProfile) {
          setSelectedAuditores([myAuditorProfile.name]);
        } else if (type === 'pessoal') {
          // Em modo pessoal, não selecionar um random - deixa vazio se não achou por nome
          setSelectedAuditores([]);
        } else {
          setSelectedAuditores(auditoresConfig.length > 0 ? [auditoresConfig[0].name] : []);
        }

        setDescricao('');
        setCategoria(CATEGORIAS[0]);
        setSetor('');
        setUnidade(unidadesConfig.length > 0 ? unidadesConfig[0].name : '');
        setHorario('09:00');
        setHorarioFim('10:00');
        setParticipantes('');
        setSelectedInternalParticipants([]);
        setIsRecorrente(false);
        setFrequencia('semanal');
        setPublicarQualidade(false);
        setPublicarHomecare(false);
        const dataFinalPadrao = new Date();
        dataFinalPadrao.setMonth(dataFinalPadrao.getMonth() + 1);
        setTerminaEm(dataFinalPadrao.toISOString().split('T')[0]);
      }
    }
  // auditoresConfig intencionalmente omitida: não deve resetar o form quando a lista de usuários carrega
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, editingAuditoria, selectedDate, unidadesConfig, CATEGORIAS, initialValues]);

  // Efeito secundário: Se o modal já está aberto em modo CRIAÇÃO e nenhum auditor
  // foi selecionado (race condition: auditoresConfig carregou depois que o modal abriu),
  // auto-seleciona o primeiro responsável disponível.
  useEffect(() => {
    if (!isOpen || editingAuditoria || selectedAuditores.length > 0) return;
    if (type === 'pessoal') return;
    if (auditoresConfig.length === 0) return;
    const myAuditorProfile = auditoresConfig.find(a => a.name === userName);
    setSelectedAuditores([myAuditorProfile ? myAuditorProfile.name : auditoresConfig[0].name]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auditoresConfig]);

  const handleHorarioChange = (e) => {
    const newHorario = e.target.value;
    setHorario(newHorario);
    if (newHorario) {
      setHorarioFim(minutesToTime(timeToMinutes(newHorario) + 60));
    }
  };

  const handleApplyTime = (newStart, newEnd) => {
    setHorario(newStart);
    setHorarioFim(newEnd);
  };

  // Monta lista de UIDs para verificar (participantes internos + auditores com UID)
  const getParticipantsUidsForCheck = () => {
    const uids = [...selectedInternalParticipants];
    if (type !== 'pessoal') {
      selectedAuditores.forEach(audName => {
        const user = systemUsers.find(u => u.name === audName);
        if (user && !uids.includes(user.id)) uids.push(user.id);
      });
    }
    return uids;
  };

  const getDateStr = () => {
    if (data && typeof data.toISOString === 'function') return data.toISOString().split('T')[0];
    if (typeof data === 'string') return data;
    return '';
  };

  // Se não estiver aberto, não renderiza nada (Evita travamento de UI)
  if (!isOpen) {
    if (confirmDelete) setConfirmDelete(false);
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Relaxa validação se for um aceite de convite (indicado por disableSystemInvite)
    // E relaxa também se for do tipo 'pessoal', pois o dono sempre será o usuário master da agenda dele.
    if (selectedAuditores.length === 0 && !disableSystemInvite && type !== 'pessoal') {
      alert('Selecione pelo menos um responsável.');
      return;
    }

    setIsSubmitting(true);
    try {
      let dataStringParaSalvar = '';
      if (data && typeof data.toISOString === 'function') {
        dataStringParaSalvar = data.toISOString().split('T')[0];
      } else if (typeof data === 'string') {
        dataStringParaSalvar = data; // Fallback se já for string
      } else {
         throw new Error("Data inválida ou não selecionada.");
      }

      const atividadeData = {
        auditor: selectedAuditores.length > 0 ? selectedAuditores[0] : '',
        auditores: selectedAuditores,
        descricao,
        categoria,
        setor,
        unidade,
        horario,
        horarioFim,
        data: dataStringParaSalvar,
        participantes,
        participantesUids: selectedInternalParticipants,
        participantesNomes: systemUsers
          .filter(u => selectedInternalParticipants.includes(u.id))
          .map(u => u.name),
        auditoria: descricao,
        conviteSistema: disableSystemInvite ? false : (selectedInternalParticipants.length > 0 || selectedAuditores.length > 0),
        publicarQualidade: type === 'pessoal' ? publicarQualidade : undefined,
        publicarHomecare: type === 'pessoal' ? publicarHomecare : undefined,
      };

      const recorrenciaData = {
        isRecorrente,
        frequencia,
        terminaEm,
      };

      if (editingAuditoria) {
        await onUpdateAuditoria(editingAuditoria, atividadeData);
      } else {
        await onAddAuditoria({ ...atividadeData, status: 'Agendado' }, recorrenciaData);
      }
      onClose();
    } catch (error) {
      console.error("Erro ao salvar auditoria/compromisso:", error);
      alert(error.message || "Erro ao salvar. Verifique sua conexão e tente novamente.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setIsSubmitting(true);
    try {
      if (onDeleteAuditoria) {
        await onDeleteAuditoria(editingAuditoria.id);
      }
    } catch (e) {
      console.error('Erro no modal ao deletar:', e);
    } finally {
      setIsSubmitting(false);
      setConfirmDelete(false);
      onClose();
    }
  };

  const handleDateChange = (e) => {
    const dateString = e.target.value;
    const [year, month, day] = dateString.split('-').map(Number);
    setData(new Date(year, month - 1, day));
  };

  const toggleAuditorSelection = (name) => {
    setSelectedAuditores(prev => {
      if (prev.includes(name)) {
        return prev.filter(a => a !== name);
      } else {
        return [...prev, name];
      }
    });
  };

  const toggleInternalParticipant = (userId) => {
    setSelectedInternalParticipants(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const handleAddEquipe = (e) => {
    const equipeName = e.target.value;
    if (!equipeName) return;
    const usersDaEquipe = systemUsers.filter(u => u.equipe === equipeName).map(u => u.id);
    setSelectedInternalParticipants(prev => {
      const newSelection = [...prev];
      usersDaEquipe.forEach(id => {
        if (!newSelection.includes(id)) newSelection.push(id);
      });
      return newSelection;
    });
    e.target.value = ''; // reseta o select
  };

  const handleGerarConvite = () => {
    const dataFormatada = data.toLocaleDateString('pt-BR');

    const textoConvite = `*Agendamento Qualidade*\n\n` +
      `*Atividade:* ${descricao}\n` +
      `*Categoria:* ${categoria}\n` +
      `*Data:* ${dataFormatada}\n` +
      `*Horário:* ${horario}\n` +
      (unidade ? `*Unidade:* ${unidade}\n` : '') +
      (setor ? `*Setor:* ${setor}\n` : '') +
      `*Auditores:* ${selectedAuditores.join(', ')}\n` +
      (participantes ? `*Obs/Participantes:* ${participantes}` : '');

    setGeneratedInviteText(textoConvite);
    setShowInviteModal(true);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>
          {type === 'pessoal' ? 'Meu Compromisso' : (editingAuditoria ? 'Detalhes da Atividade' : 'Agendar Nova Atividade')}
        </h2>

        {/* Compromisso que veio de um convite/e-mail (Tela de E-mail): link da reunião e de onde veio */}
        {editingAuditoria?.origem === 'email' && (
          <div style={{ background: editingAuditoria.status === 'Cancelado' ? '#fef2f2' : '#eff6ff', border: `1px solid ${editingAuditoria.status === 'Cancelado' ? '#fecaca' : '#bfdbfe'}`, borderRadius: 10, padding: '10px 12px', marginBottom: 12, fontSize: 13, display: 'flex', flexDirection: 'column', gap: 4 }}>
            <strong style={{ color: editingAuditoria.status === 'Cancelado' ? '#b91c1c' : '#1d4ed8' }}>
              {editingAuditoria.status === 'Cancelado' ? 'Reunião cancelada pelo organizador' : 'Veio de um convite por e-mail'}
            </strong>
            {editingAuditoria.link && editingAuditoria.status !== 'Cancelado' && (
              <a href={editingAuditoria.link} target="_blank" rel="noopener noreferrer"
                style={{ alignSelf: 'flex-start', background: '#42796e', color: '#fff', padding: '6px 12px', borderRadius: 8, textDecoration: 'none', fontWeight: 600 }}>
                Entrar na reunião
              </a>
            )}
            {editingAuditoria.local && <span>Local: {editingAuditoria.local}</span>}
            {editingAuditoria.organizador && <span>Organizador: {editingAuditoria.organizador}</span>}
            {editingAuditoria.participantesEmail?.length > 0 && <span>Participantes: {editingAuditoria.participantesEmail.map(p => p.nome || p.email).join(', ')}</span>}
            {editingAuditoria.emailAssunto && <span style={{ color: '#6b7280' }}>E-mail: {editingAuditoria.emailAssunto} ({editingAuditoria.emailConta})</span>}
          </div>
        )}


        <form onSubmit={handleSubmit}>
          <div className="form-group-row">
            <div className="form-group">
              <label>Data</label>
              <input type="date" value={data.toISOString().split('T')[0]} onChange={handleDateChange} required />
            </div>
            <div className="form-group">
              <label>Horário Inicial</label>
              <input type="time" value={horario} onChange={handleHorarioChange} required />
            </div>
            <div className="form-group">
              <label>Horário Final</label>
              <input type="time" value={horarioFim} onChange={(e) => setHorarioFim(e.target.value)} required />
            </div>
          </div>


          {!editingAuditoria && (
            <div className="recorrencia-section" style={{ marginBottom: '15px', padding: '10px', background: '#f8f9fa', borderRadius: '5px' }}>
              <div className="form-group" style={{ marginBottom: '5px' }}>
                <label className="checkbox-label" style={{ fontWeight: 'bold' }}>
                  <input
                    type="checkbox"
                    checked={isRecorrente}
                    onChange={(e) => setIsRecorrente(e.target.checked)}
                  />
                  Repetir esta atividade
                </label>
              </div>
              {isRecorrente && (
                <div className="form-group-row" style={{ marginTop: '10px' }}>
                  <div className="form-group">
                    <label>Frequência</label>
                    <select value={frequencia} onChange={(e) => setFrequencia(e.target.value)}>
                      <option value="diaria">Diariamente</option>
                      <option value="semanal">Semanalmente</option>
                      <option value="quinzenal">Quinzenalmente</option>
                      <option value="mensal">Mensalmente</option>
                      <option value="anual">Anualmente</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Termina em</label>
                    <input type="date" value={terminaEm} onChange={(e) => setTerminaEm(e.target.value)} required />
                  </div>
                </div>
              )}
            </div>
          )}


          {/* Publicação em outras agendas — apenas no tipo pessoal e sem edição */}
          {type === 'pessoal' && !editingAuditoria && (canPublishQualidade || canPublishHomecare) && (
            <div style={{ display: 'flex', gap: 16, padding: '10px 14px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, marginBottom: 14, flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#0369a1', alignSelf: 'center' }}>Publicar também em:</span>
              {canPublishQualidade && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600, color: '#0f766e' }}>
                  <input type="checkbox" checked={publicarQualidade} onChange={e => setPublicarQualidade(e.target.checked)} />
                  📋 Agenda da Qualidade
                </label>
              )}
              {canPublishHomecare && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600, color: '#7c3aed' }}>
                  <input type="checkbox" checked={publicarHomecare} onChange={e => setPublicarHomecare(e.target.checked)} />
                  🏠 Agenda Homecare
                </label>
              )}
            </div>
          )}

          <div className="form-group">
            <label>Descrição da Atividade</label>
            <input
              type="text"
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              required
              autoFocus
              placeholder={type === 'pessoal' ? 'Descrição do seu compromisso...' : "Ex: Auditoria de Processos, Treinamento de Higiene..."}
            />
          </div>

          <div className="form-group-row">
            {unidadesConfig.length > 0 && (
              <div className="form-group">
                <label>Unidade</label>
                <select value={unidade} onChange={(e) => setUnidade(e.target.value)}>
                  {unidadesConfig.map(u => <option key={u.id} value={u.name}>{u.name}</option>)}
                </select>
              </div>
            )}
            <div className="form-group">
              <label>Setor</label>
              <input type="text" value={setor} onChange={(e) => setSetor(e.target.value)} placeholder="Opcional" />
            </div>
          </div>

          {/* MULTISELECT DE AUDITORES / RESPONSÁVEIS */}
          <div className="form-group">
            <label>Responsáveis (Selecione um ou mais)</label>
            <div className="auditor-selection-container" style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '10px',
              marginTop: '8px',
              border: '1px solid #ddd',
              padding: '12px',
              borderRadius: '8px',
              maxHeight: type === 'pessoal' ? '80px' : '200px',
              overflowY: 'auto',
              backgroundColor: '#fff'
            }}>
              {/* Em modo pessoal, mostrar apenas o usuário atual como responsável */}
              {(() => {
                const listToShow = type === 'pessoal'
                  ? auditoresConfig.filter(a => a.name === userName).slice(0, 1)
                  : auditoresConfig;
                return listToShow.length > 0 ? listToShow.map(aud => (
                  <label
                    key={aud.id}
                    className="auditor-checkbox-item"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      cursor: type === 'pessoal' ? 'default' : 'pointer',
                      fontSize: '0.9rem',
                      padding: '5px 10px',
                      borderRadius: '20px',
                      backgroundColor: selectedAuditores.includes(aud.name) ? '#26a69a' : '#f0f0f0',
                      color: selectedAuditores.includes(aud.name) ? '#fff' : '#333',
                      border: selectedAuditores.includes(aud.name) ? '1px solid #00796b' : '1px solid #ddd',
                      fontWeight: selectedAuditores.includes(aud.name) ? '600' : 'normal',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedAuditores.includes(aud.name)}
                      onChange={() => type !== 'pessoal' && toggleAuditorSelection(aud.name)}
                      style={{ marginRight: '5px', display: type === 'pessoal' ? 'none' : 'inline' }}
                    />
                    <span style={{ backgroundColor: aud.color, width: '8px', height: '8px', borderRadius: '50%', display: 'inline-block', marginRight: '6px', flexShrink: 0 }}></span>
                    {aud.name}
                  </label>
                )) : (
                  <p className="empty-msg-small" style={{ margin: 0, color: '#999' }}>Nenhum responsável configurado.</p>
                );
              })()}
            </div>
          </div>

          {/* PARTICIPANTES INTERNOS - com picker popup */}
          <div className="form-group">
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              marginBottom: '8px', 
              flexWrap: 'wrap', 
              gap: '12px' 
            }}>
              <label style={{ margin: 0, minWidth: '100px' }}>Participantes Internos</label>
              
              <div style={{ 
                display: 'flex', 
                gap: '8px', 
                alignItems: 'center',
                flexWrap: 'wrap',
                justifyContent: 'flex-end',
                flex: 1
              }}>
                {equipesDisponiveis.length > 0 && (
                  <select 
                    onChange={handleAddEquipe}
                    style={{
                      fontSize: '0.8rem', padding: '4px 8px', borderRadius: '4px',
                      border: '1px solid #ddd', cursor: 'pointer', outline: 'none'
                    }}
                  >
                    <option value="">+ Adicionar Equipe</option>
                    {equipesDisponiveis.map(eq => (
                      <option key={eq} value={eq}>{eq}</option>
                    ))}
                  </select>
                )}

                <button
                  type="button"
                  onClick={() => { setParticipantSearch(''); setShowParticipantPicker(true); }}
                  style={{
                    fontSize: '0.8rem', padding: '4px 12px', borderRadius: '20px',
                    backgroundColor: '#00897b', color: '#fff', border: 'none', cursor: 'pointer'
                  }}
                >
                  + Selecionar
                </button>
              </div>
            </div>
            {/* Tags dos selecionados */}
            <div style={{
              minHeight: '40px', border: '1px solid #ddd', borderRadius: '4px',
              padding: '8px', display: 'flex', flexWrap: 'wrap', gap: '6px', backgroundColor: '#fafafa'
            }}>
              {selectedInternalParticipants.length === 0 ? (
                <span style={{ color: '#aaa', fontSize: '0.85rem' }}>Nenhum participante selecionado</span>
              ) : (
                selectedInternalParticipants.map(uid => {
                  const user = systemUsers.find(u => u.id === uid);
                  if (!user) return null;
                  return (
                    <span key={uid} style={{
                      display: 'inline-flex', alignItems: 'center', gap: '5px',
                      backgroundColor: '#e0f2f1', border: '1px solid #26a69a',
                      borderRadius: '20px', padding: '3px 10px', fontSize: '0.82rem',
                      color: '#00695c', fontWeight: '500'
                    }}>
                      {user.name}
                      <span
                        onClick={() => toggleInternalParticipant(uid)}
                        style={{ cursor: 'pointer', fontWeight: 'bold', opacity: 0.7, marginLeft: '2px' }}
                      >×</span>
                    </span>
                  );
                })
              )}
            </div>
          </div>

          {/* Botão Verificar Agendas */}
          {getParticipantsUidsForCheck().length > 0 && (
            <div style={{ textAlign: 'center', margin: '8px 0 16px' }}>
              <button
                type="button"
                onClick={() => setShowAvailabilityPanel(true)}
                style={{
                  background: 'linear-gradient(135deg, #00695c, #00897b)',
                  color: '#fff', border: 'none', borderRadius: '25px',
                  padding: '10px 24px', fontWeight: 700, fontSize: '0.9rem',
                  cursor: 'pointer', boxShadow: '0 4px 14px rgba(0,80,157,0.3)',
                  display: 'inline-flex', alignItems: 'center', gap: '8px'
                }}
              >
                🔍 Verificar Agendas Disponíveis
              </button>
            </div>
          )}

          <div className="form-group">
            <label>Participantes Externos / Observações</label>
            <textarea
              placeholder="Digite os nomes dos participantes externos ou observações relevantes..."
              value={participantes}
              onChange={(e) => setParticipantes(e.target.value)}
              rows={3}
            ></textarea>
          </div>

          {/* PAINEL DE RSVP (CONFIRMAÇÕES) - Somente em modo edição */}
          {editingAuditoria && (editingAuditoria.participantesUids?.length > 0 || editingAuditoria.aceitePorUids?.length > 0 || editingAuditoria.recusas?.length > 0) && (
            <div style={{
              margin: '20px 0', padding: '15px', backgroundColor: '#f8f9fa',
              border: '1px solid #e9ecef', borderRadius: '8px'
            }}>
              <h4 style={{ margin: '0 0 15px 0', color: '#495057', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                📋 Painel de Confirmações (RSVP)
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                
                {/* Confirmados */}
                {(() => {
                  const acceptedIds = editingAuditoria.aceitePorUids || [];
                  const acceptedNames = editingAuditoria.aceitePor || [];
                  // Mescla IDs e nomes caso seja legado
                  const confirmedList = [...new Set([
                    ...acceptedIds.map(uid => {
                      const u = systemUsers.find(su => su.id === uid);
                      return u ? u.name : null;
                    }).filter(Boolean),
                    ...acceptedNames
                  ])];

                  return confirmedList.length > 0 ? (
                    <div>
                      <strong style={{ color: '#28a745', fontSize: '0.85rem' }}>✅ Confirmados ({confirmedList.length}):</strong>
                      <div style={{ fontSize: '0.85rem', color: '#555', marginTop: '4px' }}>{confirmedList.join(', ')}</div>
                    </div>
                  ) : null;
                })()}

                {/* Recusados */}
                {(() => {
                  const recusas = editingAuditoria.recusas || [];
                  return recusas.length > 0 ? (
                    <div>
                      <strong style={{ color: '#dc3545', fontSize: '0.85rem' }}>❌ Recusados ({recusas.length}):</strong>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '4px' }}>
                        {recusas.map((r, idx) => (
                          <div key={idx} style={{ fontSize: '0.85rem', color: '#555', backgroundColor: '#ffe6e6', padding: '4px 8px', borderRadius: '4px' }}>
                            <b>{r.nome}:</b> {r.justificativa || 'Sem justificativa'}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null;
                })()}

                {/* Pendentes */}
                {(() => {
                  const allUids = editingAuditoria.participantesUids || [];
                  const acceptedIds = editingAuditoria.aceitePorUids || [];
                  const recusasIds = (editingAuditoria.recusas || []).map(r => r.uid);
                  
                  const pendingUids = allUids.filter(uid => !acceptedIds.includes(uid) && !recusasIds.includes(uid));
                  
                  if (pendingUids.length === 0) return null;

                  const pendingNames = pendingUids.map(uid => {
                    const u = systemUsers.find(su => su.id === uid);
                    return u ? u.name : 'Usuário Desconhecido';
                  });

                  return (
                    <div>
                      <strong style={{ color: '#fd7e14', fontSize: '0.85rem' }}>⏳ Aguardando Resposta ({pendingNames.length}):</strong>
                      <div style={{ fontSize: '0.85rem', color: '#555', marginTop: '4px' }}>{pendingNames.join(', ')}</div>
                    </div>
                  );
                })()}
                
              </div>
            </div>
          )}

          <div className="modal-actions" style={{ flexWrap: 'wrap', gap: '8px' }}>
            {/* Botões de Status - só em modo edição */}
            {editingAuditoria && !editingAuditoria.readOnly && (
              <>
                <button type="button" onClick={async () => {
                  if (onStatusChange) {
                    onStatusChange('Feito');
                  } else if (onUpdateAuditoria) {
                    try {
                      await onUpdateAuditoria(editingAuditoria, {
                        status: 'Feito',
                        dataConclusao: new Date().toISOString().split('T')[0],
                        justificativaNaoRealizado: null
                      });
                      onClose();
                    } catch (error) {
                      console.error("Erro ao mudar status:", error);
                      alert("Erro ao atualizar status.");
                    }
                  }
                }} style={{
                  backgroundColor: '#28a745', color: 'white', border: 'none',
                  padding: '8px 16px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold'
                }}>✅ Realizado</button>
                <button type="button" onClick={() => setShowJustificativa(true)} style={{
                  backgroundColor: '#dc3545', color: 'white', border: 'none',
                  padding: '8px 16px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold'
                }}>❌ Não Realizado</button>
              </>
            )}

            {editingAuditoria && onDeleteAuditoria && (
              confirmDelete ? (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <button type="button" className="btn-cancel" onClick={() => setConfirmDelete(false)} style={{ padding: '8px 15px', color: '#555', background: '#e0e0e0', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                    Cancelar
                  </button>
                  <button type="button" onClick={handleDelete} className="btn-danger" style={{ padding: '8px 15px', color: '#fff', background: '#f44336', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>
                    Confirmar Exclusão
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="btn-danger"
                  onClick={() => setConfirmDelete(true)}
                  style={{ opacity: editingAuditoria.readOnly ? 0.8 : 1 }}
                  disabled={editingAuditoria.readOnly}
                >
                  {editingAuditoria.readOnly ? 'Remover da Minha Agenda' : 'Excluir Agendamento'}
                </button>
              )
            )}
            <div style={{ flexGrow: 1 }}></div>

            {!editingAuditoria?.readOnly && (
              <>
                <button type="button" onClick={handleGerarConvite} className="btn-save" style={{ backgroundColor: '#00897b' }} disabled={isSubmitting}>Convite (WhatsApp)</button>
              </>
            )}

            <button type="button" onClick={onClose} className="btn-cancel" disabled={isSubmitting}>
              {editingAuditoria?.readOnly ? 'Fechar' : 'Cancelar'}
            </button>

            {!editingAuditoria?.readOnly && (
              <button type="submit" className="btn-save" style={{ flex: 1 }} disabled={isSubmitting}>
                {isSubmitting ? 'Salvando...' : (submitLabel || (editingAuditoria ? 'Salvar Alterações' : 'Salvar e Enviar Convites'))}
              </button>
            )}
          </div>

          {/* Painel de Justificativa para Não Realizado */}
          {showJustificativa && (
            <div style={{
              marginTop: '15px', padding: '15px', backgroundColor: '#fff3cd',
              border: '1px solid #ffc107', borderRadius: '8px'
            }}>
              <h4 style={{ margin: '0 0 10px', color: '#856404' }}>Justificativa - Não Realizado</h4>
              <textarea
                value={justificativaText}
                onChange={(e) => setJustificativaText(e.target.value)}
                placeholder="Informe o motivo pelo qual o agendamento não foi realizado..."
                rows="3"
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
              />
              <div style={{ marginTop: '10px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => { setShowJustificativa(false); setJustificativaText(''); }}
                  style={{ padding: '6px 16px', borderRadius: '4px', border: '1px solid #ddd', cursor: 'pointer' }}
                >Cancelar</button>
                <button type="button" onClick={async () => {
                  if (!justificativaText.trim()) { alert('Informe a justificativa.'); return; }
                  if (onStatusChange) {
                    onStatusChange('Não Realizado', justificativaText);
                  } else if (onUpdateAuditoria) {
                    try {
                      await onUpdateAuditoria(editingAuditoria, {
                        status: 'Não Realizado',
                        justificativaNaoRealizado: justificativaText,
                        dataConclusao: null
                      });
                      onClose();
                    } catch (error) {
                      console.error("Erro ao salvar justificativa:", error);
                      alert("Erro ao salvar justificativa.");
                    }
                  }
                  setShowJustificativa(false);
                  setJustificativaText('');
                }}
                  style={{ padding: '6px 16px', borderRadius: '4px', backgroundColor: '#dc3545', color: 'white', border: 'none', cursor: 'pointer', fontWeight: 'bold' }}
                >Confirmar</button>
              </div>
            </div>
          )}
        </form>
      </div>

      {/* ===== PARTICIPANT PICKER POPUP ===== */}
      {showParticipantPicker && (
        <div className="modal-overlay" style={{ zIndex: 3000 }} onClick={() => setShowParticipantPicker(false)}>
          <div className="modal-content" style={{ maxWidth: '500px', width: '95%', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ marginBottom: '12px', fontSize: '1.1rem', color: '#333' }}>Selecionar Participantes</h3>
            <input
              type="text"
              placeholder="Buscar por nome ou email..."
              value={participantSearch}
              onChange={e => setParticipantSearch(e.target.value)}
              style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', marginBottom: '12px', fontSize: '0.9rem' }}
              autoFocus
            />
            <div style={{ overflowY: 'auto', flex: 1, border: '1px solid #eee', borderRadius: '6px' }}>
              {systemUsers
                .filter(u => {
                  const q = participantSearch.toLowerCase();
                  return !q || u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q);
                })
                .map(u => {
                  const isSelected = selectedInternalParticipants.includes(u.id);
                  return (
                    <label key={u.id} style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      padding: '10px 14px', cursor: 'pointer',
                      backgroundColor: isSelected ? '#e0f2f1' : 'transparent',
                      borderBottom: '1px solid #f0f0f0',
                      transition: 'background 0.15s'
                    }}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleInternalParticipant(u.id)}
                      />
                      <div>
                        <div style={{ fontWeight: isSelected ? '600' : 'normal', color: isSelected ? '#00695c' : '#333', fontSize: '0.9rem' }}>
                          {u.name}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#888' }}>
                          {u.email}
                          {u.equipe && <span style={{ marginLeft: '6px', backgroundColor: '#e9ecef', padding: '1px 6px', borderRadius: '10px', color: '#555' }}>Equipe: {u.equipe}</span>}
                        </div>
                      </div>
                    </label>
                  );
                })
              }
            </div>
            <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <span style={{ fontSize: '0.85rem', color: '#555', alignSelf: 'center' }}>
                {selectedInternalParticipants.length} selecionado(s)
              </span>
              <button
                type="button"
                onClick={() => setShowParticipantPicker(false)}
                style={{ padding: '8px 20px', borderRadius: '6px', backgroundColor: '#00897b', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: '600' }}
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}

      <InviteModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        inviteText={generatedInviteText}
      />

      <AvailabilityPanel
        isOpen={showAvailabilityPanel}
        onClose={() => setShowAvailabilityPanel(false)}
        date={getDateStr()}
        startTime={horario}
        endTime={horarioFim}
        participantsUids={getParticipantsUidsForCheck()}
        systemUsers={systemUsers}
        excludeEventId={editingAuditoria ? editingAuditoria.id : null}
        onApplyTime={handleApplyTime}
      />
    </div>
  );
}

export default AddAuditoriaModal;